import {
  Flex,
  Header,
  HeaderTitle,
  HeaderUpperContent,
  Icon,
  LinkAnchor,
  pageEntranceSpringValues,
} from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { animated, useSpring } from 'react-spring'

import { getCurrentQuote, getInsuredProfile, Profile } from '../../../../../../domain'
import { useFormValidation } from '../../../../../../hooks/adjustments/formValidation'
import {
  useAdditionalProfileFormData,
  useAdditionalProfileProposalAdjustment,
} from '../../../../../../hooks/adjustments/proposal'
import {
  TExtends,
  TKey,
  useWriskTranslation,
} from '../../../../../../infrastructure/internationalisation'
import { useRequiredParams } from '../../../../../../infrastructure/routing'
import { AdditionalProfileDisclosureConfig } from '../../../../../../state/configuration'
import { FormErrors } from '../../../../../formBuilder'
import { useProposal } from '../../../proposalContext'

const tKey = TKey('pages.additional-profile')

export interface AdditionalProfileDisclosureProps {
  section: AdditionalProfileDisclosureConfig
  isLastSection: boolean
}

const Content: FunctionComponent<
  AdditionalProfileDisclosureProps & { profile: Profile }
> = ({ section, isLastSection, profile }) => {
  const spring = useSpring(pageEntranceSpringValues)
  const navigate = useNavigate()

  const { t } = useWriskTranslation()

  const data = useAdditionalProfileFormData(section.inputs, profile)
  const onSubmitAdjustment = useAdditionalProfileProposalAdjustment({
    inputs: section.inputs,
    applyRating: isLastSection,
    profile: profile,
    onComplete: () => navigate('..'),
  })

  const onValidateForm = useFormValidation({
    validationProfile: section.validationProfile,
    onSuccess: onSubmitAdjustment.execute,
  })

  return (
    <animated.div style={spring}>
      <Header>
        <HeaderUpperContent>
          <Flex justifyContent='flex-start' display='inline-flex' as={Link} to='..'>
            <Icon size='small' pl={2} icon='chevronRight' rotate={180} />
            <LinkAnchor fontFamily='buttons' variant='standalone' typoSize='sm'>
              {t([tKey('actions.back'), 'defaults.actions.back'])}
            </LinkAnchor>
          </Flex>
        </HeaderUpperContent>
        <HeaderTitle>{t(tKey('header'))}</HeaderTitle>
      </Header>
      <FormErrors
        id={section.id}
        key={section.id}
        footerText={section.footerText}
        data={data}
        inputs={section.inputs}
        onSubmit={onValidateForm.execute}
        loading={onValidateForm.loading}
        tKey={TExtends(tKey, 'form')}
        t={t}
      />
    </animated.div>
  )
}

export const AdditionalProfileDisclosure: FunctionComponent<
  AdditionalProfileDisclosureProps
> = ({ section, isLastSection }) => {
  const { proposal } = useProposal()

  const { profileCode } = useRequiredParams()

  const quote = getCurrentQuote(proposal)

  const profile = getInsuredProfile(quote, profileCode)

  return profile ? (
    <Content section={section} isLastSection={isLastSection} profile={profile} />
  ) : (
    <Navigate to='..' />
  )
}

export const AdditionalProfileCreateDisclosure: FunctionComponent<
  AdditionalProfileDisclosureProps
> = ({ section, isLastSection }) => {
  return (
    <Content
      section={section}
      isLastSection={isLastSection}
      profile={{ profileCode: 'p1', data: {} }}
    />
  )
}
